import { Directive, Input, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { UserService } from 'app/core/user/user.service';
import { Subject, takeUntil } from 'rxjs';

@Directive({
    selector: '[hasAnyAuthority]',
    standalone: true
})
export class HasAnyAuthorityDirective {
    private authorities: string[] = [];
    private _userService = inject(UserService);
    private templateRef = inject(TemplateRef<any>);
    private viewContainerRef = inject(ViewContainerRef);
    private _unsubscribeAll: Subject<any> = new Subject<any>();

    @Input()
    set hasAnyAuthority(value: string | string[]) {
        // Handle both array and comma-separated string inputs
        if (Array.isArray(value)) {
            this.authorities = value.map(v => v.trim().toUpperCase());
        } else if (typeof value === 'string') {
            this.authorities = value ? value.split(',').map(v => v.trim().toUpperCase()) : [];
        } else {
            this.authorities = [];
        }

        this.subscribeToUser();
    }

    private subscribeToUser(): void {
        this._userService.user$.pipe(takeUntil(this._unsubscribeAll)) // Automatically unsubscribe when component is destroyed
            .subscribe((user) => {
                this.viewContainerRef.clear();

                // if no authority is provided, we are rendering by default
                if (!this.authorities || this.authorities.length === 0) {
                    this.viewContainerRef.createEmbeddedView(this.templateRef);
                    return;
                }

                if (user && user.authorities) {
                    // Convert user authorities to lowercase for comparison
                    const userAuthorities = user.authorities.map((a: string) => a.toUpperCase());

                    const hasAuthority = this.authorities.some(auth =>
                        userAuthorities.includes(auth));

                    // If the user has permission, render the embedded template
                    // Otherwise, the view container remains empty (nothing will be rendered)
                    if (hasAuthority) {
                        this.viewContainerRef.createEmbeddedView(this.templateRef);
                    }
                }
            });
    }
}
